import { AsyncPipe, DOCUMENT, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId, TextQaId } from '@app/helpers';
import { UserService } from '@app/services';
import { Store } from '@ngrx/store';
import { DataLayerService, DlEventName, DlPaybackService, ExactagService } from 'haba-base-components';
import {
  AuthService,
  I18nModule,
  Order,
  RoutingService,
  StateWithUser,
  UrlModule,
} from 'haba-core';
import { EmporixCheckoutService, getFormattedPriceValue } from 'haba-emporix';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
@Component({
  selector: 'app-confirmation-thank-you-message',
  templateUrl: './order-confirmation-thank-you-message.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./order-confirmation-thank-you-message.component.scss'],
  imports: [NgIf, SvgComponent, RouterLink, AsyncPipe, I18nModule, UrlModule],
})
export class OrderConfirmationThankYouMessageComponent implements OnInit, OnDestroy {
  orderDetails$: Observable<{ orderEmail: string; orderTotal: string; guestOrder: boolean }>;
  public isNewRegisteredUser = false;
  toHomeButtonQaId: ButtonQaId;
  emailTextQaId: TextQaId;
  orderTotalTextQaId: TextQaId;
  private destroy$ = new Subject();
  public isLogin$ = this.userService.getCurrentUser().pipe(map(Boolean));
  public isCompany$ = this.userService
    .getCurrentUser()
    .pipe(map((user) => Boolean(user?.isCompany)));

  constructor(
    private checkoutService: EmporixCheckoutService,
    private userService: UserService,
    private store: Store<StateWithUser>,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router,
    private routingService: RoutingService,
    private exactagService: ExactagService,
    private dataLayerService: DataLayerService,
    private dlPaybackService: DlPaybackService,
    @Inject(DOCUMENT) public document: Document
  ) {}

  ngOnInit() {
    let orderGuid = this.route.snapshot.queryParams.orderId;
    if (!orderGuid) {
      orderGuid = new URLSearchParams(this.document.location.search).get('orderId');
    }
    this.orderDetails$ = this.checkoutService.getOrderDetails().pipe(
      filter((orderDetails) => !!orderDetails),
      filter((orderDetails) => orderDetails.code === orderGuid),
      tap((orderDetails) => {
        this.dataLayerService.setDataLayerFromEvent(DlEventName.PURCHASE, orderDetails);
        this.dlPaybackService.pushPurchaseEventForPayback(orderDetails);
        this.exactagService.runExactag(environment.exactag, this.router.url, orderDetails);

        // prefixbox analytics
        orderDetails.entries?.forEach((item) => {
          (window as any).prefixboxAnalytics?.('orderitem', 'serp', {
            productId: item.product?.code,
            title: item.product?.name,
            url: item.product?.url,
            category: item.product?.categories,
            itemPrice: item.product?.price?.value,
            currency: item.product?.price?.currencyIso,
            itemCount: item.quantity,
          });
        });
        (window as any).prefixboxAnalytics?.('order', 'serp', {
          revenue: orderDetails.totalPrice?.value,
          currency: orderDetails.totalPrice?.currencyIso,
        });
      }),
      map((orderDetails: Order) => ({
        orderEmail: orderDetails.user.uid ? orderDetails.user.uid : orderDetails['email'],
        orderTotal: orderDetails.totalPriceWithTax?.formattedValue
          ? orderDetails.totalPriceWithTax?.formattedValue
          : getFormattedPriceValue(
              (orderDetails['totalPrice'] as number) || 0,
              orderDetails['currency'],
              orderDetails['countyCode']
            ),
        guestOrder: orderDetails.guestCustomer,
      }))
    );

    this.isNewRegisteredUser = this.getIsNewRegisteredUser();
    if (this.isNewRegisteredUser) this.deleteIsNewRegisteredUser();

    this.initIds();
  }

  logout() {
    this.isLogin$.pipe(takeUntil(this.destroy$)).subscribe((isLogin) => {
      if (isLogin) this.authService.logout();
    });
  }

  ngOnDestroy() {
    this.destroy$.next('bye');
    this.destroy$.complete();
    this.checkoutService.clearCheckoutData();
    this.checkoutService.clearPlacedOrder();
  }

  get myOrdersUrl(): string {
    return this.routingService.getUrl({ cxRoute: 'myAccountMyOrders' });
  }

  get orderStatusUrl(): string {
    return this.routingService.getUrl({ cxRoute: 'orderStatus' });
  }

  initIds() {
    const baseId = HabaQaId.forPage(HabaQaId.page.orderConfirmation);
    this.toHomeButtonQaId = baseId.getButtonId(HabaQaId.elements.button.home);
    this.emailTextQaId = baseId.getTextId(HabaQaId.elements.text.email);
    this.orderTotalTextQaId = baseId.getTextId(HabaQaId.elements.text.total);
  }

  private getIsNewRegisteredUser() {
    try {
      const isNewRegisteredUser = JSON.parse(localStorage.getItem('isNewRegisteredUser'));
      return Boolean(isNewRegisteredUser);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  }

  private deleteIsNewRegisteredUser() {
    try {
      localStorage.removeItem('isNewRegisteredUser');
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      /* empty */
    }
  }
}
